import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWizard } from 'react-use-wizard';

import { FundingPolicyType } from '@shared/appModel';
import { formatCurrency } from '@shared/formatCurrency';

import { Alert, AlertDescription } from '@design-system';

import {
  useAddPolicyFromQuote,
  usePolicyTotalCost,
  getPolicyTypeLabel,
  useFundingSources,
} from '@/features/guided-financial-planning/hooks';
import {
  ASSET_TYPES,
  ASSET_TYPE_TO_ACCOUNT_TYPE,
  type AssetTypeId,
} from '@/features/guided-financial-planning/config';
import {
  useSession,
  useSessionMutations,
  useIsSessionComplete,
} from '@/features/guided-financial-planning/context';
import {
  useAllAllocations,
  useIsFullyFunded,
  usePolicyCost,
  getFundingAllocationActions,
} from '@/features/guided-financial-planning/stores';
import { StepLayout } from '@/features/guided-financial-planning/components/step-layout';
import { SelectedPolicyCard } from '@/features/guided-financial-planning/components/selected-policy-card';
import { FundingSourcesList } from '@/features/guided-financial-planning/components/funding-sources-list';
import {
  FundingSourceDialog,
  type FundingSourceDialogPayload,
} from '@/features/guided-financial-planning/components/funding-source-dialog';
import { hasRecurringPremiumStructure } from '@/features/guided-financial-planning/funding-disclaimer';

export function QuotesFundingStep() {
  const { nextStep, previousStep } = useWizard();
  const { session } = useSession();
  const { updateStepData, isSaving } = useSessionMutations();
  const isSessionComplete = useIsSessionComplete();

  // Get self-funding sources to calculate total available assets (snapshot for completed sessions)
  const fundingSources = useFundingSources();
  const selfFunding = fundingSources?.selfFunding ?? {};

  const totalAvailableAssets = useMemo(() => {
    return Object.values(selfFunding).reduce((sum, source) => {
      const value = (source as SelfFundingSource).existingAssetsValue;
      return sum + (value ?? 0);
    }, 0);
  }, [selfFunding]);

  // Get the first selected quote from the hook
  const { selectedQuote: selectedQuoteInfo } = useAddPolicyFromQuote();
  const quoteId = selectedQuoteInfo?.quoteId ?? null;
  const selectedQuote = selectedQuoteInfo?.quote ?? null;

  // Calculate total policy cost using calcClientDerivatives
  const calculatedPolicyCost = usePolicyTotalCost();

  // Store selectors
  const allAllocations = useAllAllocations();
  const isFullyFunded = useIsFullyFunded();
  const policyCost = usePolicyCost();

  // Initialize store with active quote context
  useEffect(() => {
    getFundingAllocationActions().setActiveQuote(quoteId, calculatedPolicyCost);
  }, [quoteId, calculatedPolicyCost]);

  // Sync disabled state to store
  useEffect(() => {
    getFundingAllocationActions().setDisabled(isSessionComplete);
  }, [isSessionComplete]);

  // Sync store from session on mount/session change
  useEffect(() => {
    const sessionAllocations = session?.stepData?.policyFundingAllocations;
    if (sessionAllocations) {
      getFundingAllocationActions().initFromSession(
        sessionAllocations as Record<string, Record<string, number>>,
      );
    }
  }, [session?.stepData?.policyFundingAllocations]);

  const handleBack = async () => {
    if (quoteId) {
      await updateStepData({ policyFundingAllocations: allAllocations });
      getFundingAllocationActions().markClean();
    }
    previousStep();
  };

  const handleNext = async () => {
    if (quoteId) {
      await updateStepData({ policyFundingAllocations: allAllocations });
      getFundingAllocationActions().markClean();
    }
    nextStep();
  };

  // Dialog state for add/edit funding sources
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogPayload, setDialogPayload] = useState<FundingSourceDialogPayload | null>(null);

  const handleEditSource = useCallback(
    (selfFundingSourceId: string, selfFundingSource: SelfFundingSource) => {
      const accountType = selfFundingSource.accountType ?? '';
      const assetTypeId = (Object.keys(ASSET_TYPE_TO_ACCOUNT_TYPE) as AssetTypeId[]).find(
        key => ASSET_TYPE_TO_ACCOUNT_TYPE[key] === accountType,
      );
      const label =
        selfFundingSource.selfFundingName ??
        ASSET_TYPES.find(a => a.id === assetTypeId)?.label ??
        'Account';

      setDialogPayload({
        mode: 'edit',
        fundingSourceId: selfFundingSourceId,
        assetTypeId: assetTypeId ?? (accountType as AssetTypeId),
        label,
        selfFundingSource,
      });
      setDialogOpen(true);
    },
    [],
  );

  const handleAddSource = useCallback((assetTypeId: AssetTypeId, label: string) => {
    setDialogPayload({ mode: 'add', assetTypeId, label });
    setDialogOpen(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    // When a new account was added, sync the asset type into session.selectedAssets
    if (dialogPayload?.mode === 'add') {
      const currentAssets = session?.stepData?.selectedAssets ?? [];
      if (!currentAssets.includes(dialogPayload.assetTypeId)) {
        updateStepData({
          selectedAssets: [...currentAssets, dialogPayload.assetTypeId],
        });
      }
    }
    setDialogOpen(false);
    setDialogPayload(null);
  }, [dialogPayload, session?.stepData?.selectedAssets, updateStepData]);

  const ccrcProductName = selectedQuoteInfo?.ccrcTypeAProduct?.productName;
  const insurancePolicyLabel = selectedQuote
    ? getPolicyTypeLabel(selectedQuote.fundingPolicyType as FundingPolicyType)
    : undefined;
  const policyTypeLabel = ccrcProductName ?? insurancePolicyLabel ?? 'the policy';

  if (!selectedQuoteInfo || !quoteId) {
    return (
      <StepLayout
        title="How will you fund this policy?"
        subtitle={`Select which accounts you'll use to pay for the ${policyTypeLabel}. You can split the cost across multiple accounts.`}
        onBack={handleBack}
      >
        <div className="mx-auto max-w-2xl">
          <p className="text-center text-gray-500">No policy selected.</p>
        </div>
      </StepLayout>
    );
  }

  const hasInsufficientFunds = totalAvailableAssets < policyCost;

  return (
    <StepLayout
      title="How will you fund this policy?"
      subtitle={`Select which accounts you'll use to pay for the ${policyTypeLabel}. You can split the cost across multiple accounts.`}
      onBack={handleBack}
      onNext={handleNext}
      isNextDisabled={!isFullyFunded}
      nextButtonLabel="Calculate Impact"
      isLoading={isSaving}
    >
      {hasInsufficientFunds && (
        <div className="mx-auto mb-4 max-w-2xl">
          <Alert variant="warning">
            <AlertDescription>
              Your available assets ({formatCurrency(totalAvailableAssets)}) don&apos;t cover the
              policy cost ({formatCurrency(policyCost)}). Edit your account balances or add a new
              funding account below.
            </AlertDescription>
          </Alert>
        </div>
      )}
      <div className="mx-auto max-w-2xl space-y-6">
        <SelectedPolicyCard />
        <FundingSourcesList
          onEditSource={isSessionComplete ? undefined : handleEditSource}
          onAddSource={isSessionComplete ? undefined : handleAddSource}
          disableAdd={isSessionComplete}
          showPolicyDeductionAssumption={hasRecurringPremiumStructure(
            selectedQuoteInfo.policyFundingSource,
          )}
        />
      </div>
      <FundingSourceDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        payload={dialogPayload}
      />
    </StepLayout>
  );
}
